import { Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/modal';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import SearchSelector from 'components/fields/SearchSelector';
import { getCustomer } from 'interfaces/customer';
import {
    addFullTooPerceiveTransaction,
    addTransaction,
    cancelTransaction,
    getNonAffectedCreditNoteByCustomer,
    getTooPerceiveByCustomer,
} from 'interfaces/invoice';
import { mergeInvoiceFields } from 'interfaces/utils';
import { useEffect, useState } from 'react';
import { dateToFrenchShort } from 'utils/date';

const MarkAsPaidModal = (props: {
    isOpen: any;
    onClose: any;
    invoice: any;
    onClick: any;
    onTransactionChange?: (invoice: any) => void;
}) => {
    const { isOpen, onClose, invoice, onClick, onTransactionChange } = props;
    const [transaction, setTransaction] = useState<{
        amount?: number;
        date?: string;
        paymentMethod?: string;
        reference?: string;
    }>({ amount: undefined, date: '', paymentMethod: '', reference: '' });
    const [localInvoice, setLocalInvoice] = useState<any>(invoice);
    const [nonAffectedCreditNotes, setNonAffectedCreditNotes] = useState([]);
    const [tooPerceiveInvoices, setTooPerceiveInvoices] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isImputing, setIsImputing] = useState(false);
    const [customerPaymentMethod, setCustomerPaymentMethod] = useState('');

    const handleChanges = (e: any) => {
        const { id, value } = e.target;
        setTransaction((prev: any) => ({ ...prev, [id]: value }));
    };

    // Merge the invoice returned by the backend after a transaction is
    // added/canceled into local state, so the modal reflects the new
    // "reste à payer" immediately without closing/reopening.
    const applyInvoiceUpdate = (updated: any) => {
        if (!updated) return;
        setLocalInvoice((prev: any) => mergeInvoiceFields(prev, updated));
        if (onTransactionChange) onTransactionChange(updated);
    };

    const cleanPaymentMethod = (paymentMethod: string) => {
        const map: Record<string, string> = {
            'cash': 'Espèces',
            'check': 'Chèque',
            'transfer': 'Virement',
            'credit-card': 'Carte bancaire',
            'direct-debit': 'Prélèvement',
            'credit-note': 'Avoir',
            'too-perceive': 'Trop perçu',
        };
        return map[paymentMethod] ?? paymentMethod;
    };

    const initCustomerPaymentMethod = async () => {
        if (!invoice || !invoice.customerId) return;
        const customer = await getCustomer(invoice.customerId);
        if (customer && customer.paymentMethod) {
            setCustomerPaymentMethod(customer.paymentMethod);
        }
    };

    const initNonAffectedCreditNotes = async () => {
        if (!invoice || !invoice.customerId) return;
        const res = await getNonAffectedCreditNoteByCustomer(
            invoice.customerId,
        );

        if (res) {
            setNonAffectedCreditNotes(res);
        }
    };

    const initTooPerceiveInvoices = async () => {
        if (!invoice || !invoice.customerId) return;
        const res = await getTooPerceiveByCustomer(invoice.customerId);
        if (res) setTooPerceiveInvoices(res);
    };

    const handleSaveTransaction = async () => {
        if (isSubmitting) return; // Prevent multiple clicks
        setIsSubmitting(true); // Disable the button

        try {
            await onClick(localInvoice.id, transaction); // Call the function passed in props
            onClose(); // Close the modal after success
        } catch (error) {
            console.error('Erreur lors de l’enregistrement de la transaction :', error);
        } finally {
            setIsSubmitting(false); // Re-enable the button after processing
        }
    };

    useEffect(() => {
        setLocalInvoice(invoice);
        setNonAffectedCreditNotes([]);
        setTooPerceiveInvoices([]);
        setCustomerPaymentMethod('');
        initNonAffectedCreditNotes();
        initTooPerceiveInvoices();
        initCustomerPaymentMethod();
        setTransaction((prev) => ({
            ...(prev ?? {}),
            amount: invoice?.leftToPay,
            date: new Date().toISOString().slice(0, 10),
            paymentMethod: '',
            reference: '',
        }));
    }, [invoice, isOpen]);

    useEffect(() => {
        if (customerPaymentMethod) {
            setTransaction((prev) => ({
                ...(prev ?? {}),
                paymentMethod: customerPaymentMethod,
            }));
        }
    }, [customerPaymentMethod]);

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="!z-[1002] !m-auto !w-max min-w-[350px] !max-w-[85%] top-[3vh]">
                <ModalBody>
                    <Card extra="px-[30px] pt-[25px] pb-[40px] max-w-[650px] flex flex-col !z-[1004]">
                        <>
                            <h1 className="mb-2 text-xl font-bold">
                                Marquer comme payé
                            </h1>
                            <p className="mb-5 text-sm">
                                Reste à payer: {localInvoice?.leftToPay.toFixed(2)}€
                                sur {localInvoice?.totalTTC.toFixed(2)}€{' '}
                            </p>
                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <InputField
                                    id="date"
                                    label="Date de paiement*"
                                    type="date"
                                    defaultValue={transaction?.date}
                                    inputSmall={true}
                                    onChange={handleChanges}
                                />
                                <InputField
                                    id="amount"
                                    label="Montant payé*"
                                    type="number"
                                    defaultValue={localInvoice?.leftToPay}
                                    value={transaction?.amount || ''}
                                    placeholder="1000"
                                    inputSmall={true}
                                    onChange={handleChanges}
                                />
                                <SearchSelector
                                    label="Moyen de paiement"
                                    displayKey="name"
                                    value={transaction?.paymentMethod}
                                    inputSmall={true}
                                    options={[
                                        {
                                            _id: 'cash',
                                            name: 'Espèces',
                                        },
                                        {
                                            _id: 'check',
                                            name: 'Chèque',
                                        },
                                        {
                                            _id: 'transfer',
                                            name: 'Virement',
                                        },
                                        {
                                            _id: 'credit-card',
                                            name: 'Carte bancaire',
                                        },
                                        {
                                            _id: 'direct-debit',
                                            name: 'Prélèvement',
                                        },
                                        {
                                            _id: 'credit-note',
                                            name: 'Avoir',
                                        },
                                    ]}
                                    onSelect={(e: any) => {
                                        setTransaction((prev) => ({
                                            ...(prev ?? {}),
                                            paymentMethod: e._id,
                                        }));
                                    }}
                                />
                                <InputField
                                    id="reference"
                                    label="Référence de paiement"
                                    value={transaction?.reference || ''}
                                    type="text"
                                    placeholder="RF-2021-0001"
                                    inputSmall={true}
                                    onChange={handleChanges}
                                />
                            </div>
                        </>
                        {/* List non affected credit note to apply them */}
                        {nonAffectedCreditNotes &&
                            nonAffectedCreditNotes.length > 0 && (
                                <div className="mt-2 mb-3">
                                    <p className="text-sm text-navy-700 underline mb-1">
                                        Avoirs non imputés pour ce client :
                                    </p>
                                    <div className="mt-3 max-h-24 overflow-y-auto divide-y divide-gray-100 border rounded-md bg-white shadow-sm">
                                        {nonAffectedCreditNotes.map(
                                            (creditNote: any) => (
                                                <div
                                                    key={creditNote.id}
                                                    className="flex items-center justify-between px-2 py-1.5 hover:bg-gray-50 transition"
                                                >
                                                    <span className="text-[11px] text-gray-700 text-right truncate">
                                                        Avoir #
                                                        {
                                                            creditNote.invoiceNumber
                                                        }{' '}
                                                        ·{' '}
                                                        {(-creditNote.totalTTC).toFixed(
                                                            2,
                                                        )}
                                                        € TTC
                                                    </span>
                                                    <span
                                                        onClick={async () => {
                                                            if (isImputing) return;
                                                            setIsImputing(true);
                                                            const res = await addTransaction(
                                                                localInvoice.id,
                                                                {
                                                                    amount: creditNote.totalTTC * -1,
                                                                    creditNoteId: creditNote._id,
                                                                    paymentMethod: 'credit-note',
                                                                    reference: `Avoir #${creditNote.invoiceNumber}`,
                                                                    date: new Date().toISOString().slice(0, 10),
                                                                },
                                                            );
                                                            if (res && !res.error && res.data) {
                                                                applyInvoiceUpdate(res.data);
                                                                setNonAffectedCreditNotes((prev: any) =>
                                                                    prev.filter((c: any) => c._id !== creditNote._id),
                                                                );
                                                                setTransaction((prev) => ({
                                                                    ...(prev ?? {}),
                                                                    amount: res.data.leftToPay,
                                                                    paymentMethod: '',
                                                                    reference: '',
                                                                }));
                                                            }
                                                            setIsImputing(false);
                                                        }}
                                                        className="text-[12px] text-toola-500 cursor-pointer hover:underline"
                                                    >
                                                        Imputer
                                                    </span>
                                                </div>
                                            ),
                                        )}
                                    </div>
                                </div>
                            )}

                        {tooPerceiveInvoices && tooPerceiveInvoices.length > 0 && (
                            <div className="mt-2 mb-3">
                                <p className="text-sm text-navy-700 underline mb-1">
                                    Trop perçus disponibles pour ce client :
                                </p>
                                <div className="mt-3 max-h-24 overflow-y-auto divide-y divide-gray-100 border rounded-md bg-white shadow-sm">
                                    {tooPerceiveInvoices.map((inv: any) => (
                                        <div
                                            key={inv._id}
                                            className="flex items-center justify-between px-2 py-1.5 hover:bg-gray-50 transition"
                                        >
                                            <span className="text-[11px] text-gray-700 truncate">
                                                Facture #{inv.invoiceNumber} ·{' '}
                                                {(inv.amountPaid - inv.totalTTC).toFixed(2)}€ en trop
                                            </span>
                                            <span
                                                onClick={async () => {
                                                    if (isImputing) return;
                                                    setIsImputing(true);
                                                    const res = await addFullTooPerceiveTransaction(inv._id);
                                                    if (res && !res.error) {
                                                        setTooPerceiveInvoices((prev: any) =>
                                                            prev.filter((i: any) => i._id !== inv._id),
                                                        );
                                                        // The backend picks whichever eligible invoice
                                                        // for the customer matches; only refresh the
                                                        // amount shown here if it landed on this invoice.
                                                        if (res.data?.invoice?._id === localInvoice.id) {
                                                            applyInvoiceUpdate(res.data.invoice);
                                                            setTransaction((prev) => ({
                                                                ...(prev ?? {}),
                                                                amount: res.data.invoice.leftToPay,
                                                            }));
                                                        }
                                                    }
                                                    setIsImputing(false);
                                                }}
                                                className="text-[12px] text-toola-500 cursor-pointer hover:underline"
                                            >
                                                Imputer
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {localInvoice &&
                            localInvoice.transactions &&
                            localInvoice.transactions.length > 0 && (
                                <>
                                    <h1 className="text-sm text-navy-700 underline">
                                        Paiements précédents:
                                    </h1>
                                    <div className="mt-3 max-h-[150px] overflow-x-scroll">
                                        <table className="w-full bg-gray-100 rounded-xl border-separate">
                                            <thead>
                                                <tr className="bg-white text-xs font-medium rounded-xl">
                                                    <th className="py-3">
                                                        Date
                                                    </th>
                                                    <th className="py-3">
                                                        Référence
                                                    </th>
                                                    <th className="py-3">
                                                        Méthode
                                                    </th>
                                                    <th className="py-3">
                                                        Montant
                                                    </th>
                                                    <th className="py-3">
                                                        Action
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {localInvoice.transactions.map(
                                                            (
                                                                transaction: any,
                                                                index: number,
                                                            ) => (
                                                                <tr
                                                                    key={index}
                                                                    className={
                                                                        index %
                                                                            2 !==
                                                                        0
                                                                            ? 'bg-gray-50 text-xs text-center'
                                                                            : 'bg-white text-xs text-center'
                                                                    }
                                                                >
                                                                    <td className="py-3">
                                                                        {dateToFrenchShort(
                                                                            transaction.date,
                                                                        )}
                                                                    </td>
                                                                    <td className="py-3">
                                                                        {transaction.reference ||
                                                                            'N/A'}
                                                                    </td>
                                                                    <td className="py-3">
                                                                        {cleanPaymentMethod(
                                                                            transaction.paymentMethod ||
                                                                                'N/A',
                                                                        )}
                                                                    </td>
                                                                    <td className="py-3">
                                                                        {
                                                                            transaction.amount
                                                                        }
                                                                        €
                                                                    </td>
                                                                    <td className="py-3">
                                                                        <button
                                                                            className="text-red-500 px-2"
                                                                            onClick={async () => {
                                                                                const res = await cancelTransaction(
                                                                                    localInvoice.id,
                                                                                    transaction._id,
                                                                                );
                                                                                if (res && res.data) {
                                                                                    applyInvoiceUpdate(res.data);
                                                                                    setTransaction((prev) => ({
                                                                                        ...(prev ?? {}),
                                                                                        amount: res.data.leftToPay,
                                                                                    }));
                                                                                    // Canceling may free up a credit
                                                                                    // note or restore a too-perceive
                                                                                    // invoice: refresh both lists.
                                                                                    initNonAffectedCreditNotes();
                                                                                    initTooPerceiveInvoices();
                                                                                }
                                                                            }}
                                                                        >
                                                                            Annuler
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ),
                                                        )}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        <div className="mt-8 flex gap-2 mx-auto">
                            <button
                                onClick={onClose}
                                className="linear rounded-xl border-2 border-gray-700 px-5 py-3 text-base font-medium text-gray-700 transition duration-200 hover:bg-red-600/5 active:bg-red-700/5 dark:border-red-400 dark:bg-red-400/10 dark:text-white dark:hover:bg-red-300/10 dark:active:bg-red-200/10"
                            >
                                Annuler
                            </button>
                            <button
                                className={`linear hover:bg-red600 col-span-12 ml-3 flex items-center justify-center rounded-xl bg-toola-500 px-3 py-3 text-base font-medium text-white sm:col-span-6 lg:col-span-4 xl:col-span-3 xl:col-start-12 ${
                                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                                onClick={handleSaveTransaction}
                                disabled={isSubmitting} // Disable the button
                            >
                                {isSubmitting ? 'Traitement...' : 'Enregistrer la transaction'}
                            </button>
                        </div>
                    </Card>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default MarkAsPaidModal;
